// src/components/PdfReader.jsx
import React, { useState, useEffect, useRef } from 'react';
import { getDocument, GlobalWorkerOptions } from 'pdfjs-dist';
import 'pdfjs-dist/web/pdf_viewer.css';

// Configurar el worker ESM (v4+)
GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url
).toString();

const PdfReader = () => {
  const file = 'https://floricentrastorage.blob.core.windows.net/appfloricentra/vcardwebsite/pdf/Florero1.pdf';

  const [numPages, setNumPages] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [pdfDocument, setPdfDocument] = useState(null);
  const [isRendering, setIsRendering] = useState(false);

  const canvasRef = useRef(null);
  const renderTaskRef = useRef(null);

  // Cargar PDF
  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const loadingTask = getDocument({ url: file });
        const pdf = await loadingTask.promise;
        if (cancelled) return;
        setPdfDocument(pdf);
        setNumPages(pdf.numPages);
        setPageNumber(1);
      } catch (error) {
        console.error('Error al cargar el PDF', error);
      }
    })();

    return () => {
      cancelled = true;
      if (renderTaskRef.current) renderTaskRef.current.cancel();
    };
  }, [file]);

  // Renderizar página
  useEffect(() => {
    const renderPage = async (pageNum) => {
      if (!pdfDocument || !canvasRef.current) return;

      setIsRendering(true);
      try {
        const page = await pdfDocument.getPage(pageNum);
        const scale = 1.5;
        const viewport = page.getViewport({ scale });

        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');

        // HiDPI
        const outputScale = window.devicePixelRatio || 1;
        canvas.width = Math.floor(viewport.width * outputScale);
        canvas.height = Math.floor(viewport.height * outputScale);
        canvas.style.width = `${Math.floor(viewport.width)}px`;
        canvas.style.height = `${Math.floor(viewport.height)}px`;

        const renderContext = {
          canvasContext: ctx,
          viewport,
          transform: outputScale !== 1 ? [outputScale, 0, 0, outputScale, 0, 0] : null,
        };

        const task = page.render(renderContext);
        renderTaskRef.current = task;
        await task.promise;
      } catch (err) {
        if (err?.name !== 'RenderingCancelledException') {
          console.error('Error renderizando página:', err);
        }
      } finally {
        setIsRendering(false);
        renderTaskRef.current = null;
      }
    };

    if (pdfDocument) {
      // Cancela render previo si cambias de página rápido
      if (renderTaskRef.current) renderTaskRef.current.cancel();
      renderPage(pageNumber);
    }
  }, [pdfDocument, pageNumber]);

  const prevDisabled = pageNumber <= 1 || isRendering;
  const nextDisabled = pageNumber >= numPages || isRendering;

  return (
    <div>
      <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 8, background: '#fafafa' }}>
        <canvas ref={canvasRef} id="pdf-canvas" />
      </div>
      <div style={{ marginTop: 8, display: 'flex', gap: 8, alignItems: 'center' }}>
        <button onClick={() => setPageNumber(p => p - 1)} disabled={prevDisabled}>Anterior</button>
        <span>Página {pageNumber} de {numPages || '—'}</span>
        <button onClick={() => setPageNumber(p => p + 1)} disabled={nextDisabled}>Siguiente</button>
        {isRendering && <span style={{ marginLeft: 12 }}>Renderizando…</span>}
      </div>
    </div>
  );
};

export default PdfReader;
